import { useContext } from "react";

import type { UserInputContentList } from "@/api/generated/models";
import type { ClientEventModel } from "@/api/model";
import { ThinkingAnimation, View, YStack } from "@/components";
import TranscribingAnimation from "@/components/animated/TranscribingAnimation";
import { LayoutContext } from "@/components/LayoutProvider";
import AssistantMessage from "@/conversation/AssistantMessage";
import type { CuraiMessageTypes } from "@/conversation/CuraiMessage";
import CuraiMessage from "@/conversation/CuraiMessage";
import SingleSelectInputControl from "@/conversation/InputControl/SingleSelect";
import UserMessage from "@/conversation/UserMessage";
import useMaybeCopyText from "@/hooks/useMaybeCopyText";
import { captureMessage } from "@/sentry";
import { useConversationComposition } from "@/store/conversation";
import { AssistantMessageType } from "@/types";
import { isHiddenMessage } from "@/utils/messages";

export const NoriMessage = ({
  message,
  index,
  isLatest,
  hiddenMessageIds,
  isGeneralistRx = false,
}: {
  message: ClientEventModel;
  index: number;
  isLatest: boolean;
  /** Message ids suppressed from the transcript because another surface owns them (e.g. the rx panel
   * shows the in-progress question and the consent step). */
  hiddenMessageIds?: ReadonlySet<string>;
  /** Whether this conversation's rx flow is the clinical-ai-v1-rx generalist one (new transcript). */
  isGeneralistRx?: boolean;
}) => {
  const maybeCopy = useMaybeCopyText();
  const { isThinking } = useConversationComposition();

  const {
    id,
    userId,
    content,
    role,
    feedback,
    feedbackComment,
    type,
    createdAt,
    senderPhotoUrl,
    senderFullNameWithTitle,
    clinicRequestId,
    originClinicRequestId,
    conversationId,
  } = message;

  // Suppressed because another surface owns it (the rx panel shows the in-progress question and the
  // consent step). Covers both the assistant question and the user consent answer.
  if (hiddenMessageIds?.has(id)) {
    return null;
  }

  if (role === "user") {
    // Survey answers only belong in the generalist transcript. Legacy flows keep them out of the
    // stream (the quiz lives in the Sheet); a flag-on user's older legacy conversations may still
    // receive them, so skip silently rather than warn.
    if (!isGeneralistRx && (type === "rx_input" || type === "location_input")) {
      return null;
    }
    const rawContent = content as unknown;
    // rx_input / location_input answers persist as a `{ type, text }` object rather than the usual
    // input_text array; normalize so the survey answer renders as a normal user bubble.
    const userContent: UserInputContentList | null = Array.isArray(rawContent)
      ? (rawContent as UserInputContentList)
      : rawContent != null &&
          typeof rawContent === "object" &&
          typeof (rawContent as { text?: unknown }).text === "string"
        ? [{ type: "input_text", text: (rawContent as { text: string }).text }]
        : null;

    if (!userContent) {
      captureMessage(
        `Unsupported content type ${typeof content} found while rendering user message.`,
      );
      return null;
    }
    // Don't render empty first user messages
    if (
      userContent?.[0]?.type === "input_text" &&
      userContent[0]?.text === ""
    ) {
      return isThinking ? (
        <ThinkingAnimation isVisible={isThinking} />
      ) : (
        <View />
      );
    }

    // Check if this is a transcribing message
    const textContent =
      userContent.find((part) => part.type === "input_text")?.text ?? "";
    const isTranscribing = textContent === "[Voice message]";

    return (
      <>
        {!isTranscribing && (
          <UserMessage
            message={userContent}
            conversationType="nori"
            messageId={id}
          />
        )}
        {isTranscribing && <TranscribingAnimation alignSelf="flex-end" />}
        {isLatest && isThinking && <ThinkingAnimation isVisible={isThinking} />}
      </>
    );
  }

  if (role === "assistant" || role === "system") {
    if (isHiddenMessage(type, isGeneralistRx)) {
      return null;
    }

    return (
      <YStack id={id} data-purpose="NoriMessage" aria-live="polite">
        {role === "assistant" ? (
          <AssistantMessage
            id={id}
            userId={userId}
            content={content}
            role={role}
            type={type as AssistantMessageType}
            isLatest={isLatest}
            messageId={id}
            feedback={feedback}
            feedbackComment={feedbackComment}
            maybeCopy={maybeCopy}
            createdAt={createdAt}
            clinicRequestId={clinicRequestId}
            originClinicRequestId={originClinicRequestId}
            // The conversation that produced this event, which under
            // `keep-conversations-open` is not always the one in the url: the stream renders a
            // whole group. Anything attached to the event resolves under this id, not the url's.
            conversationId={conversationId}
          />
        ) : (
          <CuraiMessage
            content={content as string}
            type={type as CuraiMessageTypes}
            messageId={id}
            senderPhotoUrl={senderPhotoUrl}
            senderFullNameWithTitle={senderFullNameWithTitle}
          />
        )}
        {isLatest && <SingleSelectInputControl marginTop="$200" />}
      </YStack>
    );
  }

  return null;
};

export default function NoriTurn({
  turn,
  index,
  isLatest,
  hiddenMessageIds,
  isGeneralistRx = false,
}: {
  turn: ClientEventModel[];
  index: number;
  isLatest: boolean;
  hiddenMessageIds?: ReadonlySet<string>;
  isGeneralistRx?: boolean;
}) {
  const {
    headerHeight,
    chatScrollViewHeight,
    initialChatInputHeight,
    chatInputHeight,
  } = useContext(LayoutContext);
  const isFirstTurn = index === 0;
  const numMessages = turn.length;
  return (
    <YStack
      gap="$300"
      {...(isFirstTurn && {
        paddingTop: headerHeight.current,
      })}
      {...(isLatest && {
        minHeight:
          chatScrollViewHeight -
          // Anchor most recent turn to the space just below the header, by subtracting header height
          headerHeight.current +
          // chatScrollViewHeight shrinks when the chat input has multiple lines or multiple_choise suggestions are used
          // We add the difference so the new message still focuses at the top.
          (chatInputHeight - initialChatInputHeight.current),
      })}
    >
      {turn.map((message, index) => {
        return (
          <NoriMessage
            key={
              message.role === "user"
                ? (message.clientId ?? message.id)
                : message.id
            }
            message={message}
            index={index}
            isLatest={isLatest && index === numMessages - 1}
            hiddenMessageIds={hiddenMessageIds}
            isGeneralistRx={isGeneralistRx}
          />
        );
      })}
    </YStack>
  );
}
